/**
 * The campaign list.
 *
 * Status is the column everything else hangs off: it decides whether a campaign
 * is still editable, whether the composer is read-only, and whether the numbers
 * beside it mean anything yet.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError, emptyPage } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import { shortDate } from "./Templates";
import type { Campaign, CampaignStatus, CursorPage } from "../api/types";

const PAGE_SIZE = 50;

export function Campaigns() {
  const { orgId, can } = useSession();
  const navigate = useNavigate();

  const [history, setHistory] = useState<string[]>([]);
  const cursor = history.at(-1) ?? "";

  const { data, error, loading, reload } = useAsync<CursorPage<Campaign>>(
    (signal) =>
      api.get<CursorPage<Campaign>>("/v1/campaigns", {
        query: { cursor, limit: PAGE_SIZE },
        signal,
      }),
    [orgId, cursor],
  );

  const page = data ?? emptyPage<Campaign>();
  const firstIndex = history.length * PAGE_SIZE + 1;

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Campaigns</h1>
        <div className="row" style={{ gap: "var(--oc-space-3)" }}>
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
          {can("campaigns:write") && <NewCampaign onCreated={(id) => navigate(`/campaigns/${id}`)} />}
        </div>
      </div>

      <div className="content">
        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table campaigns-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Name</span>
              <span>Status</span>
              <span>Audience</span>
              <span className="oc-table__cell--right">Recipients</span>
              <span className="oc-table__cell--right">Created</span>
            </div>

            <div className="oc-table__body">
              {error && (
                <div className="oc-empty">
                  <div className="oc-empty__title">Could not load campaigns</div>
                  <div className="oc-empty__hint">{error.message}</div>
                  <button type="button" className="oc-btn oc-btn--sm" onClick={reload}>
                    <Icon name="refresh" size={12} />
                    Try again
                  </button>
                </div>
              )}
              {loading && page.data.length === 0 && <SkeletonRows columns={5} />}

              {!error && page.data.length === 0 && !loading && <NoCampaigns />}
              {page.data.map((campaign) => (
                <Row
                  key={campaign.id}
                  campaign={campaign}
                  onOpen={() => navigate(`/campaigns/${campaign.id}`)}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">
                {page.data.length === 0
                  ? "No campaigns"
                  : `${firstIndex}–${firstIndex + page.data.length - 1}`}
              </span>
              <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={history.length === 0}
                  onClick={() => setHistory((h) => h.slice(0, -1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={!page.has_more || !page.next_cursor}
                  onClick={() => {
                    if (page.next_cursor) setHistory((h) => [...h, page.next_cursor!]);
                  }}
                >
                  Next
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({ campaign, onOpen }: { campaign: Campaign; onOpen: () => void }) {
  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-truncate" style={{ fontWeight: 500 }}>
        {campaign.name}
      </span>
      <span>
        <CampaignStatusChip status={campaign.status} />
      </span>
      <span className="oc-mono-sm oc-text-secondary oc-truncate">{audienceLabel(campaign)}</span>
      <span className="oc-table__cell--right oc-mono-sm">
        <Recipients campaign={campaign} />
      </span>
      <span className="oc-table__cell--right oc-mono-sm oc-text-secondary">
        {shortDate(campaign.created_at)}
      </span>
    </div>
  );
}

/**
 * Counts, but only once they mean something.
 *
 * A draft's totals are zero because nothing has been resolved yet, and printing
 * "0 recipients" next to a campaign nobody has sent reads as an error rather
 * than as an absence.
 */
function Recipients({ campaign }: { campaign: Campaign }) {
  if (campaign.status === "draft" || campaign.status === "scheduled") {
    return <span className="oc-text-muted">—</span>;
  }

  const queued = campaign.queued_count ?? 0;
  const skipped = campaign.skipped_count ?? 0;

  return (
    <>
      {queued}
      {skipped > 0 && <span className="oc-text-warning"> · {skipped} skipped</span>}
    </>
  );
}

/**
 * `paused` is in the enum and the database's check constraint, but no code path
 * sets it — nothing pauses a campaign today. Rendered if it ever appears rather
 * than falling through to a blank cell, but deliberately never offered as an
 * action.
 */
export function CampaignStatusChip({ status }: { status: CampaignStatus }) {
  const tone: Record<CampaignStatus, string> = {
    draft: "oc-text-secondary",
    scheduled: "oc-text-accent",
    sending: "oc-text-accent",
    sent: "oc-text-success",
    paused: "oc-text-warning",
    cancelled: "oc-text-muted",
    failed: "oc-text-danger",
  };

  return <span className={`oc-chip oc-mono-sm ${tone[status]}`}>{status}</span>;
}

export function audienceLabel(campaign: Campaign): string {
  const parts: string[] = [];

  // Counted rather than named. The list has one narrow column for this and no
  // group names loaded, and "3 groups" is the part that changes the reader's
  // expectation of the size of the send — which name they are is a question for
  // the composer.
  const groups = campaign.audience?.group_ids?.length ?? 0;
  if (groups > 0) parts.push(groups === 1 ? "1 group" : `${groups} groups`);

  if (campaign.audience?.tag_id) parts.push("tag");
  if (parts.length === 0) return "not set";
  return parts.join(" + ");
}

function NewCampaign({ onCreated }: { onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Campaign>("/v1/campaigns", { name: name.trim() });
      setName("");
      setOpen(false);
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the campaign.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="oc-btn oc-btn--primary" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} />
        New campaign
      </button>
    );
  }

  return (
    <form className="row" style={{ gap: "var(--oc-space-2)" }} onSubmit={(event) => void submit(event)}>
      <input
        className="oc-input"
        style={{ width: 200 }}
        autoFocus
        placeholder="March newsletter"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      />
      <button type="submit" className="oc-btn oc-btn--primary" disabled={busy || !name.trim()}>
        {busy ? "Creating…" : "Create"}
      </button>
      <button type="button" className="oc-btn oc-btn--ghost" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <p className="field-error">{error}</p>}
    </form>
  );
}

// The curl is behind `keys:manage`, as on the other three screens. The consent
// sentence is not: it is the rule that decides who actually receives a campaign,
// and it belongs in front of everybody who can send one.
//
// This screen is the sharpest case of the four. Campaigns is a marketing
// screen — `campaigns:write` and `campaigns:send` are in the marketing preset
// and not the developer one — so the empty state was written almost exclusively
// for people who could not use it.
function NoCampaigns() {
  const { can } = useSession();

  if (!can("keys:manage")) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No campaigns yet</div>
        <div className="oc-empty__hint">
          A campaign sends one published template to a group of contacts. Only addresses with
          explicit consent on the channel are included — unknown consent is not permission.
          {can("campaigns:write") && " Create your first one above."}
        </div>
      </div>
    );
  }

  return (
    <div className="oc-empty">
      <div className="oc-empty__title">No campaigns yet</div>
      <div className="oc-empty__hint">
        A campaign sends one published template version to a group or a tag. Only addresses with
        explicit consent on the channel are included — unknown consent is not permission.
      </div>
      <pre className="oc-code">{`curl -X POST ${window.location.origin}/v1/campaigns \\
  -H "Authorization: Bearer $OPENCOMMS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"March newsletter"}'`}</pre>
    </div>
  );
}
